import React, { useMemo } from "react";

function toDate(value) {
  if (!value) return null;

  try {
    const parsed =
      typeof value?.toDate === "function"
        ? value.toDate()
        : new Date(value);

    return Number.isNaN(parsed.getTime())
      ? null
      : parsed;
  } catch {
    return null;
  }
}

function formatDateTime(value) {
  const parsed = toDate(value);

  return parsed
    ? parsed.toLocaleString()
    : "Date unavailable";
}

function titleCase(value) {
  return String(value || "updated")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) =>
      character.toUpperCase()
    );
}

function safeText(value, fallback = "") {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return fallback;
  }

  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return fallback;
    }
  }

  return String(value);
}

function getTone(action) {
  const normalized = String(action || "")
    .trim()
    .toLowerCase();

  if (
    normalized.includes("delete") ||
    normalized.includes("cancel") ||
    normalized.includes("no show") ||
    normalized.includes("no_show") ||
    normalized.includes("failed")
  ) {
    return {
      color: "#b91c1c",
      background: "#fee2e2",
      border: "#fecaca",
    };
  }

  if (
    normalized.includes("assign") ||
    normalized.includes("offer") ||
    normalized.includes("request")
  ) {
    return {
      color: "#1d4ed8",
      background: "#dbeafe",
      border: "#bfdbfe",
    };
  }

  if (
    normalized.includes("complete") ||
    normalized.includes("arrive") ||
    normalized.includes("paid") ||
    normalized.includes("approved")
  ) {
    return {
      color: "#166534",
      background: "#dcfce7",
      border: "#bbf7d0",
    };
  }

  if (
    normalized.includes("auto") ||
    normalized.includes("system")
  ) {
    return {
      color: "#7c3aed",
      background: "#ede9fe",
      border: "#ddd6fe",
    };
  }

  return {
    color: "#475569",
    background: "#f1f5f9",
    border: "#cbd5e1",
  };
}

export default function Shift360Audit({ shift }) {
  const auditEvents = useMemo(() => {
    if (!shift) return [];

    const rows = [];

    function add({
      id,
      action,
      date,
      actor,
      details,
      source,
    }) {
      const parsedDate = toDate(date);

      if (!parsedDate) return;

      rows.push({
        id,
        action,
        date: parsedDate,
        actor: safeText(actor, "System"),
        details: safeText(details),
        source: safeText(source, "shift"),
      });
    }

    add({
      id: "created",
      action: "Shift Created",
      date: shift?.createdAt,
      actor:
        shift?.createdByEmail ||
        shift?.createdByName ||
        shift?.createdBy ||
        shift?.createdByUid,
      details: `Role: ${safeText(
        shift?.role ||
          shift?.requiredPosition,
        "Unspecified"
      )}`,
      source: shift?.source || "planner",
    });

    add({
      id: "offered",
      action: "Shift Offered",
      date: shift?.offeredAt,
      actor:
        shift?.offeredByName ||
        shift?.offeredBy ||
        shift?.offeredByUid,
      details: `Offered workers: ${
        Array.isArray(shift?.offeredWorkerUids)
          ? shift.offeredWorkerUids.length
          : Array.isArray(shift?.offeredTo)
          ? shift.offeredTo.length
          : 0
      }`,
      source: "assignment",
    });

    add({
      id: "requested",
      action: "Shift Requested",
      date:
        shift?.requestedAt ||
        shift?.requestSubmittedAt,
      actor:
        shift?.requestedByName ||
        shift?.requestedBy ||
        shift?.requestedWorkerUid,
      details: "Worker request recorded.",
      source: "worker",
    });

    add({
      id: "assigned",
      action: "Worker Assigned",
      date: shift?.assignedAt,
      actor:
        shift?.assignedByName ||
        shift?.assignedBy ||
        shift?.assignedByUid,
      details: safeText(
        shift?.assignedWorkerName ||
          shift?.assignedWorkerUid ||
          shift?.workerId,
        "Worker ID unavailable"
      ),
      source:
        shift?.assignmentSource ||
        shift?.assignMode ||
        "assignment",
    });

    add({
      id: "reassigned",
      action: "Worker Reassigned",
      date:
        shift?.reassignedAt ||
        shift?.lastReassignedAt,
      actor:
        shift?.reassignedByName ||
        shift?.reassignedBy ||
        shift?.reassignedByUid,
      details: shift?.reassignReason
        ? `Reason: ${shift.reassignReason}`
        : "Worker assignment was changed.",
      source: "reassignment",
    });

    add({
      id: "time-confirmed",
      action: "Shift Time Confirmed",
      date: shift?.timeConfirmedAt,
      actor: shift?.timeConfirmedBy,
      details: safeText(
        shift?.timeSlot ||
          shift?.originalTimeSlot,
        "Custom shift time"
      ),
      source: "planner",
    });

    add({
      id: "activated",
      action: "Shift Activated",
      date: shift?.activatedAt,
      actor:
        shift?.activatedBy ||
        shift?.assignedWorkerUid,
      details: "Worker activated the shift.",
      source: "worker",
    });

    add({
      id: "tracking",
      action: "Worker On My Way",
      date:
        shift?.trackingAt ||
        shift?.onMyWayAt ||
        shift?.enRouteAt,
      actor:
        shift?.trackingBy ||
        shift?.assignedWorkerUid,
      details:
        "Worker travel status was recorded.",
      source: "worker",
    });

    add({
      id: "arrived",
      action: "Worker Arrived",
      date:
        shift?.arrivedAt ||
        shift?.checkedInAt,
      actor:
        shift?.arrivedBy ||
        shift?.assignedWorkerUid,
      details:
        shift?.arrivalDistanceMeters !== undefined
          ? `Arrival distance: ${safeText(
              shift.arrivalDistanceMeters
            )} meters`
          : "Facility arrival was recorded.",
      source: "worker",
    });

    add({
      id: "completed",
      action:
        shift?.autoCompleted === true
          ? "Shift Auto Completed"
          : "Shift Completed",
      date: shift?.completedAt,
      actor:
        shift?.completedBy ||
        shift?.completedByUid ||
        shift?.assignedWorkerUid,
      details:
        shift?.autoCompleted === true
          ? "Completed automatically after scheduled end."
          : "Completion was recorded.",
      source:
        shift?.autoCompleted === true
          ? "automation"
          : "worker",
    });

    add({
      id: "no-show",
      action: "Worker Marked No Show",
      date: shift?.noShowAt,
      actor:
        shift?.noShowBy ||
        shift?.noShowByUid,
      details:
        shift?.noShowReason ||
        "No-show status recorded.",
      source: "operations",
    });

    add({
      id: "cancelled",
      action: "Shift Cancelled",
      date:
        shift?.cancelledAt ||
        shift?.canceledAt,
      actor:
        shift?.cancelledBy ||
        shift?.canceledBy ||
        shift?.cancelledByUid,
      details:
        shift?.cancellationReason ||
        shift?.cancelReason ||
        "Cancellation recorded.",
      source:
        shift?.cancellationSource ||
        "operations",
    });

    add({
      id: "payroll",
      action: `Payroll ${titleCase(
        shift?.payrollStatus || "Updated"
      )}`,
      date:
        shift?.payrollUpdatedAt ||
        shift?.paidAt,
      actor:
        shift?.payrollUpdatedBy ||
        shift?.paidBy,
      details: `Payroll ID: ${safeText(
        shift?.payrollId ||
          shift?.payrollRecordId,
        "Not available"
      )}`,
      source: "payroll",
    });

    add({
      id: "invoice",
      action: `Invoice ${titleCase(
        shift?.invoiceStatus || "Updated"
      )}`,
      date:
        shift?.invoiceUpdatedAt ||
        shift?.invoicedAt,
      actor:
        shift?.invoiceUpdatedBy ||
        shift?.invoicedBy,
      details: `Invoice ID: ${safeText(
        shift?.invoiceId ||
          shift?.invoiceRecordId,
        "Not available"
      )}`,
      source: "invoice",
    });

    const embeddedHistory = [
      ...(Array.isArray(shift?.auditHistory)
        ? shift.auditHistory
        : []),
      ...(Array.isArray(shift?.activityHistory)
        ? shift.activityHistory
        : []),
      ...(Array.isArray(shift?.history)
        ? shift.history
        : []),
    ];

    embeddedHistory.forEach((event, index) => {
      if (!event || typeof event !== "object") {
        return;
      }

      add({
        id:
          event?.id ||
          `embedded-audit-${index}`,
        action:
          event?.action ||
          event?.event ||
          event?.type ||
          "Shift Updated",
        date:
          event?.createdAt ||
          event?.timestamp ||
          event?.date ||
          event?.updatedAt,
        actor:
          event?.actorName ||
          event?.createdByName ||
          event?.actor ||
          event?.createdBy ||
          event?.userId,
        details:
          event?.details ||
          event?.description ||
          event?.reason ||
          event?.message,
        source:
          event?.source ||
          event?.channel ||
          "history",
      });
    });

    return rows.sort(
      (a, b) =>
        b.date.getTime() - a.date.getTime()
    );
  }, [shift]);

  if (!shift) return null;

  return (
    <div style={{ display: "grid", gap: 13 }}>
      <div>
        <div
          style={{
            color: "#0f172a",
            fontSize: 18,
            fontWeight: 950,
          }}
        >
          Audit History
        </div>

        <div
          style={{
            marginTop: 4,
            color: "#64748b",
            fontSize: 11,
            fontWeight: 700,
          }}
        >
         Administrative, worker, automation, and operational actions recorded for this shift.
        </div>
      </div>

      <div
        style={{
          padding: 12,
          borderRadius: 13,
          border: "1px solid #e2e8f0",
          background: "#f8fafc",
        }}
      >
        <div
          style={{
            color: "#64748b",
            fontSize: 9,
            fontWeight: 900,
            textTransform: "uppercase",
          }}
        >
          Recorded Audit Events
        </div>

        <div
          style={{
            marginTop: 4,
            color: "#0f172a",
            fontSize: 19,
            fontWeight: 950,
          }}
        >
          {auditEvents.length}
        </div>
      </div>

      {auditEvents.length === 0 ? (
        <div
          style={{
            padding: 18,
            borderRadius: 13,
            border: "1px dashed #cbd5e1",
            background: "#f8fafc",
            color: "#64748b",
            textAlign: "center",
            fontSize: 11,
            fontWeight: 750,
          }}
        >
          No dated audit events are available.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 9 }}>
          {auditEvents.map((event, index) => {
            const tone = getTone(event.action);

            return (
              <div
                key={`${event.id}-${index}`}
                style={{
                  padding: 12,
                  borderRadius: 13,
                  border: `1px solid ${tone.border}`,
                  background: "#ffffff",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent:
                      "space-between",
                    alignItems: "flex-start",
                    gap: 9,
                  }}
                >
                  <div
                    style={{
                      color: tone.color,
                      fontSize: 11,
                      fontWeight: 950,
                    }}
                  >
                    {titleCase(event.action)}
                  </div>

                  <span
                    style={{
                      padding: "4px 7px",
                      borderRadius: 999,
                      color: tone.color,
                      background: tone.background,
                      fontSize: 8,
                      fontWeight: 950,
                      textTransform: "uppercase",
                    }}
                  >
                    {titleCase(event.source)}
                  </span>
                </div>

                <div
                  style={{
                    marginTop: 4,
                    color: "#64748b",
                    fontSize: 9,
                    fontWeight: 750,
                  }}
                >
                  {formatDateTime(event.date)}
                </div>

                <div
                  style={{
                    marginTop: 7,
                    color: "#475569",
                    fontSize: 10,
                    fontWeight: 700,
                    lineHeight: 1.45,
                  }}
                >
                  {event.details ||
                    "No additional details recorded."}
                </div>

                <div
                  style={{
                    marginTop: 6,
                    color: "#64748b",
                    fontSize: 9,
                    fontWeight: 750,
                  }}
                >
                  Actor: {event.actor}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div
        style={{
          color: "#64748b",
          fontSize: 9,
          fontWeight: 700,
          lineHeight: 1.45,
        }}
      >
        Shift ID: {safeText(shift?.id, "Unavailable")}
        {" · "}
        Assignment version:{" "}
        {safeText(
          shift?.assignmentVersion,
          "Not recorded"
        )}
      </div>
    </div>
  );
}